import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Theme-aligned palette — matches HeedifyTheme.css chart colors
const COLORS = ['#00A4A6', '#1B3A6B', '#8B5CF6', '#F97316', '#EF4444', '#06B6D4', '#10b981', '#FFC700'];

const DonutChartWidget = ({ data, config }) => {
    const { t, i18n } = useTranslation();
    const nameKey       = config?.config?.nameKey       || config?.nameKey       || 'label';
    const valueKey      = config?.config?.valueKey      || config?.valueKey      || 'value';
    const categoryLabels = config?.config?.categoryLabels || config?.categoryLabels || {};
    const categoryColors = config?.config?.categoryColors || config?.categoryColors || {};
    // Quand nameKey porte des codes pays ISO 3166-1 alpha-2 (ex. 'FR', 'GB'), on les
    // localise dans la langue courante via Intl.DisplayNames — un seul mécanisme couvre
    // toutes les langues, pas de table de noms à maintenir. Les catégories non-pays
    // (TEAMS, UNKNOWN, NANP) ne sont pas des codes alpha-2 et retombent sur t().
    const localizeRegion = config?.config?.localizeRegion || config?.localizeRegion || false;

    const regionNamer = useMemo(() => {
        if (!localizeRegion) return null;
        try {
            return new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' });
        } catch (e) {
            return null; // API absente (très vieux navigateur) → fallback t()/code
        }
    }, [localizeRegion, i18n.language]);

    const translatedData = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [];
        return data.map((item, index) => {
            const categoryKey  = item[nameKey] || item.label || item.name;
            let displayLabel;
            if (regionNamer && typeof categoryKey === 'string' && /^[A-Z]{2}$/.test(categoryKey)) {
                let regionName;
                try { regionName = regionNamer.of(categoryKey); } catch (e) { regionName = null; }
                // Intl renvoie le code tel quel s'il ne le connaît pas → on retombe sur t().
                displayLabel = (regionName && regionName !== categoryKey)
                    ? regionName
                    : t(categoryKey, { defaultValue: categoryKey });
            } else {
                // categoryLabels contient des CLÉS i18n (ex. 'QUICK_ANSWER', 'ANSWERED').
                // On traduit via t() ; defaultValue garde l'ancien comportement si la valeur
                // n'est pas une clé connue (pas de régression pour les autres donuts).
                const rawLabel = categoryLabels[categoryKey] || item.label || categoryKey;
                displayLabel = t(rawLabel, { defaultValue: rawLabel });
            }
            const displayColor = categoryColors[categoryKey] || item.color || COLORS[index % COLORS.length];
            return {
                ...item,
                name: displayLabel,
                displayValue: item[valueKey] || 0,
                color: displayColor
            };
        });
    }, [data, nameKey, valueKey, categoryLabels, categoryColors, t, regionNamer]);

    const total = useMemo(() =>
        translatedData.reduce((sum, e) => sum + (e.displayValue || 0), 0),
    [translatedData]);

    // Largest segment — used for center text color
    const dominant = useMemo(() =>
        translatedData.reduce((max, e) => (e.displayValue > (max?.displayValue ?? 0) ? e : max), null),
    [translatedData]);

    const dominantPct = total > 0 && dominant
        ? Math.round((dominant.displayValue / total) * 100)
        : 0;

    if (!Array.isArray(data) || data.length === 0) return null;

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Donut area — relative so the center overlay can track its real center */}
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={translatedData}
                            dataKey="displayValue"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="55%"
                            outerRadius="80%"
                            cornerRadius={3}
                            paddingAngle={1}
                            labelLine={false}
                        >
                            {translatedData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value) => [`${Math.round((value / total) * 100)}% (${value})`, '']}
                            contentStyle={{ fontSize: 13, borderColor: '#e1e1e1', borderRadius: 6 }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* Center overlay — now matches true donut center since legend is outside */}
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                    lineHeight: 1.2,
                    width: '45%',   // hole is ~55% radius → ~ring-free width for text
                    maxWidth: 140
                }}>
                    <div style={{
                        fontSize: 'clamp(18px, 5vw, 28px)',
                        fontWeight: 700,
                        color: dominant?.color || '#1B3A6B',
                    }}>
                        {dominantPct}%
                    </div>
                    <div style={{
                        fontSize: 11,
                        color: '#605E5C',
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {dominant?.name || ''}
                    </div>
                </div>
            </div>

            {/* Legend — separate from chart so the text overlay can center correctly */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '6px 16px',
                paddingTop: 8,
                flexShrink: 0
            }}>
                {translatedData.map((entry, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: '#323130',
                        whiteSpace: 'nowrap'
                    }}>
                        <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: entry.color,
                            flexShrink: 0
                        }} />
                        {entry.name}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChartWidget;
